import type { IComponentController, IScope, ITimeoutService } from "angular";
import { includes, isEqual, remove } from "lodash";
import { finalize } from "rxjs/operators";

import type { IFunctionality, ISiteEquipmentWithCoverages, IVideoStream } from "App/Modules/Models";
import { FunctionalityType } from "App/Modules/Models";

import { EquipmentClass } from "App/Modules/Models/Models/EquipmentClass";
import type { IModalService } from "App/Modules/Services";

export default class CameraConfigurationsComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$timeout", "mmModalService"];

    public siteEquipment: ISiteEquipmentWithCoverages;

    public siteId: string;

    public isEditable: boolean;

    public isSidebarCollapsed: boolean;

    public reloadingImage: boolean;

    public analogCameraEquipmentType = EquipmentClass.AnalogCamera;

    constructor(
        private readonly $scope: IScope,
        private readonly $timeout: ITimeoutService,
        private readonly modalService: IModalService,
    ) {}

    public editVideoStream(videoStream: IVideoStream) {
        this.modalService
            .open<[IVideoStream, boolean], ISiteEquipmentWithCoverages, IVideoStream>(
                "irEditVideoStream",
                [videoStream, this.isEditable],
                this.siteEquipment,
            )
            .subscribe(
                (response: IVideoStream) => {
                    if (response !== null) {
                        Object.assign(videoStream, response);

                        this.reloadingImage = true;
                        this.$timeout(() => {
                            this.reloadingImage = false;
                        }, 0);
                    } else {
                        remove(this.siteEquipment.videoStreams, videoStream);
                    }
                },
                (reason) => reason,
            );
    }

    public editFunctionality(functionality: IFunctionality) {
        let component;

        switch (functionality.type) {
            case FunctionalityType.MotionDetection:
                component = "irEditMotionDetectionFunctionality";
                break;
            case FunctionalityType.Recording:
                component = "irEditRecordingFunctionality";
                break;
            case FunctionalityType.FlameDetection:
                component = "irEditFlameDetectionFunctionality";
                break;
            case FunctionalityType.Care:
                component = "irEditCareFunctionality";
                break;
            default:
                throw new Error("Functionality type not supported");
        }

        this.modalService
            .open<[IFunctionality, string, boolean], ISiteEquipmentWithCoverages, IFunctionality>(
                component,
                [functionality, this.siteId, this.isEditable],
                this.siteEquipment,
                "lg",
                "modal-extrawide",
            )
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (newFunctionality: IFunctionality) => {
                    if (newFunctionality !== null) {
                        functionality.name = newFunctionality.name;
                        functionality.viewCoverage.downwardTilt = newFunctionality.viewCoverage.downwardTilt;

                        if (!isEqual(functionality.videoStream, newFunctionality.videoStream)) {
                            functionality.videoStream = newFunctionality.videoStream;
                        }
                    } else {
                        remove(this.siteEquipment.functionalities, functionality);
                        remove(functionality.viewCoverage.functionalities, functionality);
                    }
                },
                (reason) => reason,
            );
    }

    public displayMasterSiteEquipment(functionality: IFunctionality): boolean {
        if (
            this.siteEquipment.masterSiteEquipment &&
            !includes(this.siteEquipment.videoStreams, functionality.videoStream)
        ) {
            return true;
        }

        return false;
    }
}
